/**
 * One settings row (M31 task-16, HUB-092 §5) — extracted from
 * ConfigurationSection so the extension settings page (HUB-103) renders the
 * identical control set: the explanatory column takes the free space and the
 * control column is a fixed, right-aligned strip. `kind`, `label` and
 * `description` arrive as props; the caller decides where they come from (the
 * shared registry for hub settings, the wire `schema`/`description` for an
 * extension's).
 *
 * Secrets: the stored value never reaches the browser (`hasValue` presence
 * marker only); the typed draft lives in local state and is cleared right
 * after a successful save.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { intMapCommitRaw, intMapDrafts, type IntMapKind, type ValueKind } from '@/lib/settings-config';

/** The structural slice both wire entry types (hub + extension) satisfy. */
export interface SettingRowEntry {
  key: string;
  value?: unknown;
  source: string;
  secret: boolean;
  hasValue?: boolean;
}

export interface SettingRowProps {
  entry: SettingRowEntry;
  kind: ValueKind;
  label: string;
  description?: string;
  hint?: string;
  error?: string;
  busy: boolean;
  onSave(kind: ValueKind, raw: boolean | string): Promise<boolean>;
  onReset(): void;
}

export function SettingRow({ entry, kind, label, description, hint, error, busy, onSave, onReset }: SettingRowProps) {
  const custom = entry.source === 'setting';
  return (
    <div className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_280px] sm:gap-8">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{label}</span>
          <Badge variant={custom ? 'accent' : 'muted'}>{custom ? 'custom' : 'default'}</Badge>
        </div>
        {description && (
          <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
        )}
        <div className="font-mono text-[11px] text-muted-foreground/70">{entry.key}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      <div className="space-y-1 sm:justify-self-end sm:text-right">
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <SettingControl entry={entry} kind={kind} label={label} busy={busy} onSave={onSave} />
          {custom && (
            <Button type="button" variant="ghost" size="sm" onClick={onReset} disabled={busy}>
              Reset to default
            </Button>
          )}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </div>
  );
}

interface ControlProps {
  entry: SettingRowEntry;
  kind: ValueKind;
  label: string;
  busy: boolean;
  onSave(kind: ValueKind, raw: boolean | string): Promise<boolean>;
}

function SettingControl({ entry, kind, label, busy, onSave }: ControlProps) {
  if (entry.secret) return <SecretValueInput entry={entry} kind={kind} label={label} busy={busy} onSave={onSave} />;
  if (kind.kind === 'boolean') {
    return (
      <Switch
        aria-label={label}
        checked={entry.value === true}
        disabled={busy}
        onCheckedChange={(v) => void onSave(kind, v)}
      />
    );
  }
  if (kind.kind === 'select') {
    const current = typeof entry.value === 'string' ? entry.value : '';
    return (
      <Select
        aria-label={label}
        className="w-64"
        value={current}
        disabled={busy}
        onChange={(e) => void onSave(kind, e.target.value)}
      >
        {kind.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        {/* A stored value outside the known literals (hub newer than this
            build) renders as-is instead of silently snapping to an option. */}
        {current !== '' && !kind.options.includes(current) && (
          <option value={current}>{current} (not supported)</option>
        )}
      </Select>
    );
  }
  if (kind.kind === 'int_map') {
    return <IntMapInput entry={entry} kind={kind} label={label} busy={busy} onSave={onSave} />;
  }
  return <CommitInput entry={entry} kind={kind} label={label} busy={busy} onSave={onSave} />;
}

/**
 * HUB-127 — one whole-number field per key (e.g. per audit source). A blank
 * field means "use the general window" and is left out of the saved object;
 * saves on blur (Enter blurs) when the assembled object changed.
 */
function IntMapInput({ entry, kind, label, busy, onSave }: ControlProps & { kind: IntMapKind }) {
  const [drafts, setDrafts] = useState(() => intMapDrafts(kind, entry.value));
  // A number input holding unparseable text ("5e", "-") reports value '',
  // which would read as "omit this key". Keep the previous draft and say so.
  const [badInput, setBadInput] = useState(false);
  const commit = (input: HTMLInputElement) => {
    setBadInput(input.validity.badInput);
    if (input.validity.badInput) return;
    const raw = intMapCommitRaw(kind, entry.value, drafts);
    if (raw !== null) void onSave(kind, raw);
  };
  return (
    <div className="w-64 space-y-2">
      {kind.keys.map((key) => (
        <label key={key} className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs text-muted-foreground">{key}</span>
          <Input
            aria-label={`${label}: ${key}`}
            className="w-36"
            type="number"
            inputMode="numeric"
            min={kind.min}
            step={1}
            placeholder="general"
            value={drafts[key]}
            disabled={busy}
            onChange={(e) => {
              const input = e.currentTarget;
              setBadInput(input.validity.badInput);
              if (!input.validity.badInput) setDrafts((prev) => ({ ...prev, [key]: input.value }));
            }}
            onBlur={(e) => commit(e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
        </label>
      ))}
      {badInput && <p className="text-sm text-destructive">Enter a whole number of days.</p>}
    </div>
  );
}

/** Text/number input that PUTs on commit (blur; Enter blurs) when changed. */
function CommitInput({ entry, kind, label, busy, onSave }: ControlProps) {
  const initial =
    entry.value === undefined ? '' : kind.kind === 'json' ? JSON.stringify(entry.value) : String(entry.value);
  const [draft, setDraft] = useState(initial);
  const numeric = kind.kind === 'number' ? kind : null;
  return (
    <Input
      aria-label={label}
      className="w-64"
      type={numeric ? 'number' : 'text'}
      inputMode={numeric ? 'decimal' : undefined}
      step={numeric && !numeric.integer ? 'any' : undefined}
      value={draft}
      disabled={busy}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== initial) void onSave(kind, draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}

/**
 * Password-style input for a secret key: never prefilled, placeholder
 * "configured" when a value is stored, and the typed plaintext is dropped
 * from state the moment a save succeeds.
 */
function SecretValueInput({ entry, kind, label, busy, onSave }: ControlProps) {
  const [draft, setDraft] = useState('');
  const commit = async () => {
    if (draft === '') return;
    const ok = await onSave(kind, draft);
    if (ok) setDraft('');
  };
  return (
    <Input
      aria-label={label}
      className="w-64"
      type="password"
      autoComplete="off"
      placeholder={entry.hasValue ? 'configured' : 'not set'}
      value={draft}
      disabled={busy}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}
